import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StrategySynthesisSection from '../components/StrategySynthesisSection';
import { logFeatureView } from '../utils/userEvents';
import { canonicalizeCountry, createHierarchy } from '../utils/filterHierarchy';
import { apiFetch } from '../utils/apiClient';
import { loadValidationLabResults, saveValidationLabResults, clearValidationLabResults } from '../utils/validationLabResultsSession';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const VALIDATION_UNIVERSE_OPTIONS = [
  { value: 'ALL', label: 'All Indexed Tickers (United States + Canada)', optimalTopNMax: 100 },
  { value: 'SP500', label: 'S&P 500 (United States)', optimalTopNMax: 100 },
  { value: 'NASDAQ100', label: 'Nasdaq-100 (United States)', optimalTopNMax: 100 },
  { value: 'DOW30', label: 'Dow 30 (United States)', optimalTopNMax: 30 },
  { value: 'TSX60', label: 'S&P/TSX 60 (Canada)', optimalTopNMax: 60 },
  { value: 'TSX_COMPOSITE', label: 'S&P/TSX Composite (Canada)', optimalTopNMax: 100 },
];
const BENCHMARK_DISPLAY_DESCRIPTION =
  'S&P 500 benchmark, represented by SPY, an ETF that tracks the S&P 500.';
const SCORING_SOURCE_LABEL = 'All Indexed Tickers (United States + Canada)';
const DEFAULT_OPTIMAL_TOP_N_MIN = 5;
const DEFAULT_OPTIMAL_TOP_N_MAX = 100;
const DEFAULT_COHORT_FORWARD_HORIZONS = [1, 2, 3, 4, 6, 8, 10];
const EMPTY_FILTER_OPTIONS = Object.freeze({
  indexCode: null,
  universe_label: null,
  ranking_source: null,
  ranking_universe_code: null,
  benchmark_label: null,
  total_unique_snapshots: null,
  earliest_snapshot_date: null,
  latest_snapshot_date: null,
  countries: [],
  sectors: [],
  industries: [],
  records: [],
});
const STRATEGY_MODES = [
  {
    value: 'balanced',
    label: 'Balanced',
    description: 'Gives each stock the same amount.',
  },
  {
    value: 'growth',
    label: 'Growth',
    description: 'Gives more money to stocks with stronger Return Consistency.',
  },
  {
    value: 'conservative',
    label: 'Conservative',
    description: 'Gives more money to stocks with stronger Risk Stability.',
  },
];
const SCORING_SOURCE_TECHNICAL_NAME = 'historical_backtest_rankings';
const PROVENANCE_DISCLOSURE_TOP_N =
  'Top N uses selected_position after applying the selected filter.';
const COMPARISON_TOP_N_DISCLOSURE =
  'Top N uses the selected ranking cutoff after your filters are applied.';
const STRATEGY_TOP_N_DISCLOSURE =
  'Top N uses the selected ranking cutoff after your filters are applied.';
const STRATEGY_GLOBAL_RANK_DISCLOSURE =
  'Displayed Global Rank is the historical global rank before filtering, so gaps can appear after your filters are applied.';
const STRATEGY_SCOPE_MARKERS_DISCLOSURE =
  'The saved historical snapshot labels provide context, but they are not a complete official reconstruction of past index membership.';
const PROVENANCE_DISCLOSURE_GLOBAL_RANK =
  'Displayed Global Rank is the stored global historical_rank and may contain gaps after filtering.';
const PROVENANCE_DISCLOSURE_SCOPE_MARKERS =
  'Persisted snapshot-era scope markers are not a complete official constituent-history reconstruction.';
const PORTFOLIO_OBJECTIVES = [
  {
    value: 'balanced',
    mode: 'balanced',
    label: 'Balanced approach',
    beginnerText: 'Spreads the portfolio evenly across the selected stocks',
    secondaryText: 'Equal Weight',
  },
  {
    value: 'growth',
    mode: 'growth',
    label: 'Higher growth potential',
    beginnerText: 'Gives more weight to stocks with stronger historical return consistency',
    secondaryText: 'Return Consistency³',
  },
  {
    value: 'conservative',
    mode: 'conservative',
    label: 'Lower volatility focus',
    beginnerText: 'Gives more weight to stocks with stronger historical risk stability',
    secondaryText: 'Risk Stability³',
  },
];

function isSupportedStrategyPlanUniverse(indexCode) {
  return VALIDATION_UNIVERSE_OPTIONS.some((option) => option.value === indexCode);
}

function getUniverseOption(indexCode) {
  return (
    VALIDATION_UNIVERSE_OPTIONS.find((option) => option.value === indexCode) ||
    VALIDATION_UNIVERSE_OPTIONS[0]
  );
}

function getPublicUniverseLabel(universeCode, fallbackLabel = 'Unavailable') {
  if (typeof universeCode !== 'string') {
    return fallbackLabel;
  }

  const option = VALIDATION_UNIVERSE_OPTIONS.find((candidate) => candidate.value === universeCode);
  return option?.label || fallbackLabel;
}

function getStrategyModeOption(mode) {
  return STRATEGY_MODES.find((option) => option.value === mode) || STRATEGY_MODES[0];
}

function getPortfolioObjectiveOption(objective) {
  return (
    PORTFOLIO_OBJECTIVES.find((option) => option.value === objective) ||
    PORTFOLIO_OBJECTIVES[0]
  );
}

function getSelectedOptimalTopNBounds(indexCode) {
  const option = getUniverseOption(indexCode);
  return {
    min: DEFAULT_OPTIMAL_TOP_N_MIN,
    max: option.optimalTopNMax || DEFAULT_OPTIMAL_TOP_N_MAX,
  };
}

function getOptimalTopNBounds(result, fallbackBounds) {
  const min = Number(result?.optimal_top_n_min);
  const max = Number(result?.optimal_top_n_max);

  if (Number.isInteger(min) && Number.isInteger(max)) {
    return { min, max };
  }

  return fallbackBounds;
}

function formatTopNRange(bounds) {
  return `Top ${bounds.min} through Top ${bounds.max}`;
}

function getCohortForwardHorizons(result) {
  const rawHorizons = Array.isArray(result?.forward_return_horizons)
    ? result.forward_return_horizons
    : DEFAULT_COHORT_FORWARD_HORIZONS;

  return rawHorizons
    .map((horizon) => Number(horizon))
    .filter((horizon) => Number.isInteger(horizon) && horizon > 0);
}

function getHorizonKey(horizonYears) {
  return `${horizonYears}y`;
}

function normalizeThresholdList(thresholds) {
  if (!Array.isArray(thresholds)) {
    return [];
  }

  return Array.from(
    new Set(
      thresholds
        .map((threshold) => Number(threshold))
        .filter((threshold) => Number.isInteger(threshold))
    )
  ).sort((a, b) => a - b);
}

function getCohortThresholdsFromResult(result) {
  const metadataThresholds = normalizeThresholdList(result?.cohort_thresholds);

  if (metadataThresholds.length > 0) {
    return metadataThresholds;
  }

  return normalizeThresholdList(
    Array.isArray(result?.cohorts)
      ? result.cohorts.map((cohort) => cohort.rank_threshold)
      : []
  );
}

function isFiniteNumber(value) {
  return typeof value === 'number' && Number.isFinite(value);
}

function formatPercent(value, { showSign = false, digits = 1 } = {}) {
  if (!isFiniteNumber(value)) {
    return '—';
  }

  const prefix = showSign && value >= 0 ? '+' : '';
  return `${prefix}${value.toFixed(digits)}%`;
}

function getOptimalTopNInterpretation(results) {
  const validResults = Array.isArray(results) ? results : [];
  const highestReturn = validResults.reduce((best, result) => {
    if (!isFiniteNumber(result?.compounded_total_return_percent)) {
      return best;
    }

    return !best || result.compounded_total_return_percent > best.compounded_total_return_percent
      ? result
      : best;
  }, null);
  const lowestDrawdown = validResults.reduce((best, result) => {
    if (!isFiniteNumber(result?.max_drawdown_percent)) {
      return best;
    }

    return !best || result.max_drawdown_percent < best.max_drawdown_percent ? result : best;
  }, null);

  if (!highestReturn && !lowestDrawdown) {
    return null;
  }

  const highestReturnSentence = highestReturn
    ? `${getStrategyModeOption(highestReturn.mode).label} Top ${highestReturn.best_top_n} had the highest historical compounded return at ${formatPercent(highestReturn.compounded_total_return_percent, { showSign: true })}`
    : '';
  const lowestDrawdownSentence = lowestDrawdown
    ? `${getStrategyModeOption(lowestDrawdown.mode).label} Top ${lowestDrawdown.best_top_n} had the lowest Max Drawdown at ${formatPercent(lowestDrawdown.max_drawdown_percent)}`
    : '';

  if (highestReturn && lowestDrawdown && highestReturn.mode === lowestDrawdown.mode) {
    return `${highestReturnSentence} and the lowest Max Drawdown at ${formatPercent(lowestDrawdown.max_drawdown_percent)}.`;
  }

  return [highestReturnSentence, lowestDrawdownSentence]
    .filter(Boolean)
    .map((sentence) => `${sentence}.`)
    .join(' ');
}

function formatCountRange(minimum, maximum, noun) {
  if (!Number.isInteger(minimum) || !Number.isInteger(maximum)) {
    return null;
  }

  const suffix = noun ? ` ${noun}` : '';
  if (minimum === maximum) {
    return `${minimum}${suffix}`;
  }

  return `${minimum}–${maximum}${suffix}`;
}

function parseIntegerOrNull(value) {
  return Number.isInteger(value) ? value : null;
}

function getComparisonActualStocksUsed(cohort) {
  const minimum = parseIntegerOrNull(cohort?.effective_cohort_size_min);
  const maximum = parseIntegerOrNull(cohort?.effective_cohort_size_max);
  if (minimum === null || maximum === null) {
    return null;
  }

  return minimum === maximum ? `${minimum}` : `${minimum}–${maximum}`;
}

function hasMatchingEffectiveComparisonCohort(leftCohort, rightCohort) {
  const fields = [
    'effective_cohort_size_min',
    'effective_cohort_size_max',
    'effective_cohort_snapshot_count',
  ];

  return fields.every((field) => {
    const leftValue = parseIntegerOrNull(leftCohort?.[field]);
    const rightValue = parseIntegerOrNull(rightCohort?.[field]);
    return leftValue !== null && leftValue === rightValue;
  });
}

function hasMatchingDisplayedComparisonResults(leftCohort, rightCohort, horizons) {
  return horizons.every((horizonYears) => {
    const horizon = getHorizonKey(horizonYears);
    const leftMetrics = leftCohort?.horizons?.[horizon] || {};
    const rightMetrics = rightCohort?.horizons?.[horizon] || {};

    return (
      Object.is(leftMetrics.available_returns, rightMetrics.available_returns) &&
      Object.is(
        leftMetrics.average_total_return_percent,
        rightMetrics.average_total_return_percent
      ) &&
      Object.is(leftMetrics.win_rate_percent, rightMetrics.win_rate_percent)
    );
  });
}

function getDisplayedComparisonCohorts(result, horizons) {
  const cohorts = Array.isArray(result?.cohorts) ? [...result.cohorts] : [];
  const orderedCohorts = cohorts.sort((leftCohort, rightCohort) => {
    const leftThreshold = Number(leftCohort?.rank_threshold);
    const rightThreshold = Number(rightCohort?.rank_threshold);
    return leftThreshold - rightThreshold;
  });

  return orderedCohorts.filter((cohort, index) => {
    const threshold = parseIntegerOrNull(cohort?.rank_threshold);
    if (threshold === null) {
      return true;
    }

    return !orderedCohorts.slice(0, index).some((smallerCohort) => {
      const smallerThreshold = parseIntegerOrNull(smallerCohort?.rank_threshold);
      return (
        smallerThreshold !== null &&
        smallerThreshold < threshold &&
        hasMatchingEffectiveComparisonCohort(smallerCohort, cohort) &&
        hasMatchingDisplayedComparisonResults(smallerCohort, cohort, horizons)
      );
    });
  });
}

function hasMatchingDisplayedStrategyMetadata(leftCohort, rightCohort) {
  const fields = [
    'effective_holdings_min',
    'effective_holdings_max',
    'eligible_cohort_size_min',
    'eligible_cohort_size_max',
  ];

  return fields.every((field) => {
    const leftValue = parseIntegerOrNull(leftCohort?.[field]);
    const rightValue = parseIntegerOrNull(rightCohort?.[field]);
    return leftValue !== null && leftValue === rightValue;
  });
}

function hasMatchingDisplayedStrategyResults(leftCohort, rightCohort) {
  const fields = [
    'positive_period_count',
    'average_annual_portfolio_return_percent',
    'compounded_total_return_percent',
    'benchmark_compounded_total_return_percent',
    'outperformance_vs_benchmark_percent',
    'max_drawdown_percent',
  ];

  return fields.every((field) => Object.is(leftCohort?.[field], rightCohort?.[field]));
}

function getDisplayedStrategyCohorts(result) {
  const cohorts = Array.isArray(result?.cohorts) ? [...result.cohorts] : [];
  const orderedCohorts = cohorts.sort((leftCohort, rightCohort) => {
    const leftThreshold = Number(leftCohort?.rank_threshold);
    const rightThreshold = Number(rightCohort?.rank_threshold);
    return leftThreshold - rightThreshold;
  });

  return orderedCohorts.filter((cohort, index) => {
    const threshold = parseIntegerOrNull(cohort?.rank_threshold);
    if (threshold === null) {
      return true;
    }

    return !orderedCohorts.slice(0, index).some((smallerCohort) => {
      const smallerThreshold = parseIntegerOrNull(smallerCohort?.rank_threshold);
      return (
        smallerThreshold !== null &&
        smallerThreshold < threshold &&
        hasMatchingDisplayedStrategyMetadata(smallerCohort, cohort) &&
        hasMatchingDisplayedStrategyResults(smallerCohort, cohort)
      );
    });
  });
}

function getAnnualCohortHoldingsSummary(cohort) {
  const holdingsLabel = formatCountRange(
    parseIntegerOrNull(cohort?.effective_holdings_min),
    parseIntegerOrNull(cohort?.effective_holdings_max),
    'holdings'
  );
  const eligibleMinimum = parseIntegerOrNull(cohort?.eligible_cohort_size_min);
  const eligibleMaximum = parseIntegerOrNull(cohort?.eligible_cohort_size_max);
  const eligibleLabel = formatCountRange(eligibleMinimum, eligibleMaximum, 'eligible stocks');
  const eligibleCountLabel = formatCountRange(eligibleMinimum, eligibleMaximum, '');

  return {
    holdingsLabel,
    eligibleLabel,
    eligibleCountLabel,
  };
}

function getReturnClass(value) {
  if (!isFiniteNumber(value)) {
    return '';
  }

  return value >= 0 ? 'positive' : 'negative';
}

function getScoringSourceDisplay(result) {
  const technicalName =
    typeof result?.ranking_source === 'string' && result.ranking_source
      ? result.ranking_source
      : SCORING_SOURCE_TECHNICAL_NAME;
  const scoringUniverseLabel = getPublicUniverseLabel(
    result?.ranking_universe_code,
    SCORING_SOURCE_LABEL
  );

  return `${technicalName} (${scoringUniverseLabel})`;
}

function getSelectedFilterDisplay(result) {
  const countryLabel = canonicalizeCountry(result?.country) || 'All Countries';
  const universeLabel = getPublicUniverseLabel(
    result?.selected_universe_code || result?.index_code,
    result?.universe_label || 'Unavailable'
  );
  const sectorLabel = result?.sector || 'All Sectors';
  const industryLabel = result?.industry || 'All Industries';
  return `${countryLabel} · ${universeLabel} · ${sectorLabel} · ${industryLabel}`;
}

function ValidationLabResultDisclosures({
  topNDisclosure = PROVENANCE_DISCLOSURE_TOP_N,
  globalRankDisclosure = PROVENANCE_DISCLOSURE_GLOBAL_RANK,
  scopeMarkersDisclosure = PROVENANCE_DISCLOSURE_SCOPE_MARKERS,
}) {
  return (
    <p className="validation-lab-disclosure-text">
      {topNDisclosure} {globalRankDisclosure} {scopeMarkersDisclosure}
    </p>
  );
}

function requireUniverseResponse(result, requestUniverse, resultArrayField, analysisLabel) {
  if (!result || typeof result !== 'object') {
    throw new Error(`${analysisLabel} returned an invalid response.`);
  }

  if (result.index_code !== requestUniverse) {
    throw new Error(
      `${analysisLabel} returned ${result.index_code || 'an unknown universe'} while ${requestUniverse} was selected.`
    );
  }

  if (result.ranking_source !== SCORING_SOURCE_TECHNICAL_NAME) {
    throw new Error(
      `${analysisLabel} returned ranking source ${result.ranking_source || 'unknown'} instead of ${SCORING_SOURCE_TECHNICAL_NAME}.`
    );
  }

  if (result.ranking_universe_code !== 'ALL') {
    throw new Error(
      `${analysisLabel} returned scoring-source universe ${getPublicUniverseLabel(
        result.ranking_universe_code,
        result.ranking_universe_code || 'unknown'
      )} instead of ${SCORING_SOURCE_LABEL}.`
    );
  }

  if (result.selected_universe_code !== requestUniverse) {
    throw new Error(
      `${analysisLabel} returned selected filter ${getPublicUniverseLabel(
        result.selected_universe_code,
        result.selected_universe_code || 'unknown'
      )} while ${getPublicUniverseLabel(requestUniverse, requestUniverse)} was requested.`
    );
  }

  if (!Array.isArray(result[resultArrayField])) {
    throw new Error(`${analysisLabel} returned an invalid ${resultArrayField} payload.`);
  }

  if (result[resultArrayField].length === 0) {
    throw new Error('No matching report rows were found for the selected filters.');
  }

  if (
    result.cohort_thresholds !== undefined &&
    normalizeThresholdList(result.cohort_thresholds).length === 0
  ) {
    throw new Error(`${analysisLabel} returned invalid cohort threshold metadata.`);
  }
}

function buildValidationLabRequestParams(filters, extraParams = {}) {
  const normalizedFilters = normalizeValidationLabFilters(filters);
  const params = {
    index_code: normalizedFilters.indexCode,
  };

  if (normalizedFilters.country) {
    params.country = normalizedFilters.country;
  }
  if (normalizedFilters.sector) {
    params.sector = normalizedFilters.sector;
  }
  if (normalizedFilters.industry) {
    params.industry = normalizedFilters.industry;
  }

  return { ...params, ...extraParams };
}

function requireFilterResponse(result, requestFilters, analysisLabel) {
  const expected = normalizeValidationLabFilters(requestFilters);
  const received = normalizeValidationLabFilters({
    indexCode: result?.index_code,
    country: result?.country,
    sector: result?.sector,
    industry: result?.industry,
  });

  if (
    received.country !== expected.country ||
    received.sector !== expected.sector ||
    received.industry !== expected.industry
  ) {
    throw new Error(`${analysisLabel} returned results for different filters.`);
  }
}

function resultMatchesFilters(result, filters) {
  const expected = normalizeValidationLabFilters(filters);
  const received = normalizeValidationLabFilters({
    indexCode: result?.index_code,
    country: result?.country,
    sector: result?.sector,
    industry: result?.industry,
  });
  return Boolean(
    result &&
    received.indexCode === expected.indexCode &&
    received.country === expected.country &&
    received.sector === expected.sector &&
    received.industry === expected.industry
  );
}

function normalizeAuthoritativeOptions(values) {
  if (!Array.isArray(values)) {
    throw new Error('Strategy Lab filter choices returned an invalid response.');
  }

  const seen = new Set();
  return values.filter((value) => {
    if (typeof value !== 'string' || !value.trim() || seen.has(value)) {
      return false;
    }
    seen.add(value);
    return true;
  });
}

function normalizeFilterValue(value) {
  if (value === null || value === undefined) {
    return '';
  }
  const normalized = String(value).trim();
  return normalized || '';
}

function normalizeValidationLabFilters(filters) {
  return {
    indexCode: normalizeFilterValue(filters?.indexCode),
    country: canonicalizeCountry(filters?.country) || '',
    sector: normalizeFilterValue(filters?.sector),
    industry: normalizeFilterValue(filters?.industry),
  };
}

function buildValidationLabPdfFilename(indexCode) {
  const slug = indexCode.toLowerCase().replace(/_/g, '-');
  const dateStamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  return `stock-scanner-validation-lab-report-${slug}-${dateStamp}.pdf`;
}

function buildStrategySynthesisPdfFilename(result) {
  const slug = String(result?.index_code || 'sp500').toLowerCase().replace(/_/g, '-');
  return `stock-scanner-validation-lab-strategy-synthesis-${slug}.pdf`;
}

function getFilenameFromContentDisposition(contentDisposition, fallbackFilename) {
  if (!contentDisposition) {
    return fallbackFilename;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/"/g, ''));
  }

  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }

  const plainMatch = contentDisposition.match(/filename=([^;]+)/i);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim().replace(/^"|"$/g, '');
  }

  return fallbackFilename;
}

function boundedErrorDetail(detail) {
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim().slice(0, 280);
  }

  if (Array.isArray(detail)) {
    const firstMessage = detail.find((item) => typeof item?.msg === 'string')?.msg;
    if (firstMessage) {
      return firstMessage.trim().slice(0, 280);
    }
  }

  return '';
}

async function validationLabRequestError(response) {
  let payload = null;
  try {
    payload = await response.json();
  } catch (_error) {
    payload = null;
  }

  const status = Number(response.status) || 0;
  const detail = boundedErrorDetail(payload?.detail);
  let message = detail;

  if (status === 401) {
    message = 'Your session has expired. Sign in again to use Strategy Lab.';
  } else if (status === 403) {
    message = 'Strategy Lab requires an active Premium subscription.';
  } else if (status === 503) {
    message = 'Strategy Lab publication data is temporarily unavailable. Try again shortly.';
  } else if (status >= 500) {
    message = 'Strategy Lab could not complete the request. Try again shortly.';
  } else if (!message) {
    message = status === 400
      ? 'The selected filters are not supported by the active publication.'
      : 'Strategy Lab could not complete the request.';
  }

  const error = new Error(message);
  error.status = status;
  return error;
}

function investorErrorMessage(error) {
  if (error instanceof TypeError) {
    return 'Strategy Lab could not reach the service. Check your connection and try again.';
  }

  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  if (!message || message === '[object Object]') {
    return 'Strategy Lab encountered an unexpected error. Try again.';
  }
  return message.slice(0, 320);
}

async function fetchValidationLabJson(path, params, signal) {
  const query = new URLSearchParams(params);
  const response = await apiFetch(`${API_BASE_URL}${path}?${query.toString()}`, {
    method: 'GET',
    signal,
  });

  if (!response.ok) {
    throw await validationLabRequestError(response);
  }

  return response.json();
}

function getValidationLabUserId(userInfo) {
  const userId = userInfo?.id ?? userInfo?.user_id;
  return userId === null || userId === undefined || userId === '' ? null : String(userId);
}

function filtersMatch(left, right) {
  return Boolean(left && right) &&
    left.indexCode === right.indexCode &&
    left.country === right.country &&
    left.sector === right.sector &&
    left.industry === right.industry;
}

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function ValidationLab() {
  const [selectedFilters, setSelectedFilters] = useState({
    indexCode: 'SP500',
    country: '',
    sector: '',
    industry: '',
  });
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTER_OPTIONS);
  const [filterOptionsStatus, setFilterOptionsStatus] = useState('initial-loading');
  const [filterOptionsError, setFilterOptionsError] = useState('');

  const [comparisonData, setComparisonData] = useState(null);
  const [comparisonLoading, setComparisonLoading] = useState(false);
  const [comparisonError, setComparisonError] = useState('');

  const [strategyResultsByMode, setStrategyResultsByMode] = useState(null);
  const [strategyLoading, setStrategyLoading] = useState(false);
  const [strategyError, setStrategyError] = useState('');
  const [activeStrategyMode, setActiveStrategyMode] = useState('balanced');
  const [selectedStrategyCohort, setSelectedStrategyCohort] = useState(10);

  const [optimalTopNData, setOptimalTopNData] = useState(null);
  const [optimalTopNLoading, setOptimalTopNLoading] = useState(false);
  const [optimalTopNError, setOptimalTopNError] = useState('');

  const [selectedPortfolioObjective, setSelectedPortfolioObjective] = useState('balanced');
  const [synthesisData, setSynthesisData] = useState(null);
  const [synthesisLoading, setSynthesisLoading] = useState(false);
  const [synthesisError, setSynthesisError] = useState('');
  const [synthesisExportingPDF, setSynthesisExportingPDF] = useState(false);
  const [synthesisExportError, setSynthesisExportError] = useState('');

  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportError, setExportError] = useState('');

  const [userInfo, setUserInfo] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);
  const [userInfoError, setUserInfoError] = useState('');

  const comparisonRequestIdRef = useRef(0);
  const comparisonAbortRef = useRef(null);
  const strategyRequestIdRef = useRef(0);
  const strategyAbortRefs = useRef([]);
  const optimalTopNRequestIdRef = useRef(0);
  const optimalTopNAbortRef = useRef(null);
  const synthesisRequestIdRef = useRef(0);
  const synthesisAbortRef = useRef(null);
  const synthesisLoadingRef = useRef(false);
  const synthesisExportRequestIdRef = useRef(0);
  const synthesisExportAbortRef = useRef(null);
  const synthesisExportingRef = useRef(false);
  const exportRequestIdRef = useRef(0);
  const exportAbortRef = useRef(null);
  const filterOptionsRequestIdRef = useRef(0);
  const filterOptionsAbortRef = useRef(null);
  const provisionalSelectionsRef = useRef(null);
  const persistedUserIdRef = useRef(null);
  const persistedSnapshotRef = useRef(null);
  const selectedFiltersRef = useRef(selectedFilters);
  const comparisonDataRef = useRef(comparisonData);
  const strategyResultsByModeRef = useRef(strategyResultsByMode);
  const activeStrategyModeRef = useRef(activeStrategyMode);
  const selectedStrategyCohortRef = useRef(selectedStrategyCohort);
  const optimalTopNDataRef = useRef(optimalTopNData);
  const selectedPortfolioObjectiveRef = useRef(selectedPortfolioObjective);
  const synthesisDataRef = useRef(synthesisData);

  const selectedUniverse = selectedFilters.indexCode;
  const selectedCountry = selectedFilters.country;
  const selectedSector = selectedFilters.sector;
  const selectedIndustry = selectedFilters.industry;
  const filterOptionsLoading =
    filterOptionsStatus === 'initial-loading' || filterOptionsStatus === 'refresh-loading';
  const filterSelectionPending =
    filterOptionsStatus === 'refresh-loading' &&
    Boolean(
      provisionalSelectionsRef.current?.country ||
      provisionalSelectionsRef.current?.sector ||
      provisionalSelectionsRef.current?.industry
    );

  const selectedUniverseOption = getUniverseOption(selectedUniverse);
  const selectedPortfolioObjectiveOption = getPortfolioObjectiveOption(selectedPortfolioObjective);
  const activeStrategyOption = getStrategyModeOption(activeStrategyMode);
  const activeStrategyData = strategyResultsByMode
    ? strategyResultsByMode[activeStrategyMode]
    : null;
  const displayedActiveStrategyCohorts = getDisplayedStrategyCohorts(activeStrategyData);
  const displayedActiveStrategyCohortThresholds = displayedActiveStrategyCohorts.map(
    (cohort) => cohort.rank_threshold
  );
  const selectedOptimalTopNBounds = getSelectedOptimalTopNBounds(selectedUniverse);
  const displayedOptimalTopNBounds = getOptimalTopNBounds(
    optimalTopNData,
    selectedOptimalTopNBounds
  );
  const optimalTopNInterpretation = getOptimalTopNInterpretation(optimalTopNData?.results);
  const selectedOptimalTopNRange = formatTopNRange(selectedOptimalTopNBounds);
  const displayedOptimalTopNRange = formatTopNRange(displayedOptimalTopNBounds);
  const displayedSynthesisData = isSupportedStrategyPlanUniverse(synthesisData?.index_code)
    ? synthesisData
    : null;
  const hierarchy = useMemo(
    () => createHierarchy(filterOptions?.records || []),
    [filterOptions]
  );
  const hierarchyReady = filterOptions?.indexCode === selectedUniverse;
  const validationUniverseMetadata = (
    filterOptionsStatus === 'ready' &&
    hierarchyReady &&
    filterOptions.universe_label &&
    filterOptions.benchmark_label &&
    filterOptions.earliest_snapshot_date &&
    filterOptions.latest_snapshot_date &&
    Number.isInteger(filterOptions.total_unique_snapshots)
  )
    ? {
      index_code: selectedUniverse,
      universe_label: filterOptions.universe_label,
      ranking_source: filterOptions.ranking_source,
      ranking_universe_code: filterOptions.ranking_universe_code,
      benchmark_label: filterOptions.benchmark_label,
      total_unique_snapshots: filterOptions.total_unique_snapshots,
      earliest_snapshot_date: filterOptions.earliest_snapshot_date,
      latest_snapshot_date: filterOptions.latest_snapshot_date,
      country: selectedCountry,
      sector: selectedSector,
      industry: selectedIndustry,
    }
    : null;
  const countryOptions = useMemo(
    () => (Array.isArray(filterOptions?.countries) ? filterOptions.countries : []),
    [filterOptions]
  );
  const sectorOptions = useMemo(
    () => (hierarchyReady ? hierarchy.getSectors(selectedUniverse, selectedCountry || null) : []),
    [hierarchy, hierarchyReady, selectedUniverse, selectedCountry]
  );
  const industryOptions = useMemo(
    () => (
      hierarchyReady
        ? hierarchy.getIndustries(selectedUniverse, selectedCountry || null, selectedSector || null)
        : []
    ),
    [hierarchy, hierarchyReady, selectedUniverse, selectedCountry, selectedSector]
  );

  const persistResultsSnapshot = (overrides = {}) => {
    const userId = persistedUserIdRef.current;
    if (!userId) {
      return;
    }

    const nextFilters = overrides.selectedFilters || selectedFiltersRef.current;
    const previousSnapshot = persistedSnapshotRef.current;
    const filtersChanged = previousSnapshot?.selectedFilters
      ? !filtersMatch(previousSnapshot.selectedFilters, nextFilters)
      : false;
    const resultOverrideProvided = [
      'comparisonData',
      'strategyResultsByMode',
      'optimalTopNData',
      'synthesisData',
    ].some((key) => hasOwn(overrides, key));
    if (filtersChanged && !resultOverrideProvided) {
      return;
    }
    const getValue = (key, currentValue) => {
      if (hasOwn(overrides, key)) {
        return overrides[key];
      }
      return filtersChanged ? null : currentValue;
    };

    const nextSnapshot = {
      selectedFilters: nextFilters,
      comparisonData: getValue('comparisonData', comparisonDataRef.current),
      strategyResultsByMode: getValue('strategyResultsByMode', strategyResultsByModeRef.current),
      activeStrategyMode: getValue('activeStrategyMode', activeStrategyModeRef.current),
      selectedStrategyCohort: getValue('selectedStrategyCohort', selectedStrategyCohortRef.current),
      optimalTopNData: getValue('optimalTopNData', optimalTopNDataRef.current),
      selectedPortfolioObjective: getValue(
        'selectedPortfolioObjective',
        selectedPortfolioObjectiveRef.current
      ),
      synthesisData: getValue('synthesisData', synthesisDataRef.current),
    };

    if (saveValidationLabResults(userId, nextSnapshot)) {
      persistedSnapshotRef.current = nextSnapshot;
    }
  };

  const beginComparisonRequest = () => {
    if (comparisonAbortRef.current) {
      comparisonAbortRef.current.abort();
    }

    const controller = new AbortController();
    const requestId = comparisonRequestIdRef.current + 1;

    comparisonRequestIdRef.current = requestId;
    comparisonAbortRef.current = controller;

    return { requestId, controller };
  };

  const cancelComparisonRequest = () => {
    comparisonRequestIdRef.current += 1;
    if (comparisonAbortRef.current) {
      comparisonAbortRef.current.abort();
      comparisonAbortRef.current = null;
    }
  };

  const beginStrategyRequests = () => {
    strategyAbortRefs.current.forEach((controller) => controller.abort());
    const controllers = STRATEGY_MODES.map(() => new AbortController());
    const requestId = strategyRequestIdRef.current + 1;

    strategyRequestIdRef.current = requestId;
    strategyAbortRefs.current = controllers;

    return { requestId, controllers };
  };

  const cancelStrategyRequests = () => {
    strategyRequestIdRef.current += 1;
    strategyAbortRefs.current.forEach((controller) => controller.abort());
    strategyAbortRefs.current = [];
  };

  const beginOptimalTopNRequest = () => {
    if (optimalTopNAbortRef.current) {
      optimalTopNAbortRef.current.abort();
    }

    const controller = new AbortController();
    const requestId = optimalTopNRequestIdRef.current + 1;

    optimalTopNRequestIdRef.current = requestId;
    optimalTopNAbortRef.current = controller;

    return { requestId, controller };
  };

  const cancelOptimalTopNRequest = () => {
    optimalTopNRequestIdRef.current += 1;
    if (optimalTopNAbortRef.current) {
      optimalTopNAbortRef.current.abort();
      optimalTopNAbortRef.current = null;
    }
  };

  const beginSynthesisRequest = () => {
    if (synthesisAbortRef.current) {
      synthesisAbortRef.current.abort();
    }

    const controller = new AbortController();
    const requestId = synthesisRequestIdRef.current + 1;

    synthesisRequestIdRef.current = requestId;
    synthesisAbortRef.current = controller;
    synthesisLoadingRef.current = true;

    return { requestId, controller };
  };

  const cancelSynthesisRequest = (updateState = true) => {
    synthesisRequestIdRef.current += 1;
    synthesisLoadingRef.current = false;
    if (synthesisAbortRef.current) {
      synthesisAbortRef.current.abort();
      synthesisAbortRef.current = null;
    }
    if (updateState) {
      setSynthesisLoading(false);
    }
  };

  const beginSynthesisExportRequest = () => {
    if (synthesisExportAbortRef.current) {
      synthesisExportAbortRef.current.abort();
    }

    const controller = new AbortController();
    const requestId = synthesisExportRequestIdRef.current + 1;

    synthesisExportRequestIdRef.current = requestId;
    synthesisExportAbortRef.current = controller;
    synthesisExportingRef.current = true;

    return { requestId, controller };
  };

  const cancelSynthesisExportRequest = (updateState = true) => {
    synthesisExportRequestIdRef.current += 1;
    synthesisExportingRef.current = false;
    if (synthesisExportAbortRef.current) {
      synthesisExportAbortRef.current.abort();
      synthesisExportAbortRef.current = null;
    }
    if (updateState) {
      setSynthesisExportingPDF(false);
    }
  };

  const beginExportRequest = () => {
    if (exportAbortRef.current) {
      exportAbortRef.current.abort();
    }

    const controller = new AbortController();
    const requestId = exportRequestIdRef.current + 1;

    exportRequestIdRef.current = requestId;
    exportAbortRef.current = controller;

    return { requestId, controller };
  };

  const cancelExportRequest = () => {
    exportRequestIdRef.current += 1;
    if (exportAbortRef.current) {
      exportAbortRef.current.abort();
      exportAbortRef.current = null;
    }
  };

  const cancelFilterOptionsRequest = () => {
    filterOptionsRequestIdRef.current += 1;
    if (filterOptionsAbortRef.current) {
      filterOptionsAbortRef.current.abort();
      filterOptionsAbortRef.current = null;
    }
  };

  const cancelActiveRequestsForFilterChange = () => {
    cancelComparisonRequest();
    cancelStrategyRequests();
    cancelOptimalTopNRequest();
    cancelSynthesisRequest();
    cancelSynthesisExportRequest();
    cancelExportRequest();
    setComparisonLoading(false);
    setStrategyLoading(false);
    setOptimalTopNLoading(false);
    setExportingPDF(false);
    setComparisonError('');
    setStrategyError('');
    setOptimalTopNError('');
    setSynthesisError('');
    setSynthesisExportError('');
    setExportError('');
  };

  const loadFilterOptions = async (requestUniverse, { retry = false } = {}) => {
    cancelFilterOptionsRequest();
    const controller = new AbortController();
    const requestId = filterOptionsRequestIdRef.current + 1;
    const isInitialLoad = filterOptionsStatus === 'initial-loading' && !retry;

    filterOptionsRequestIdRef.current = requestId;
    filterOptionsAbortRef.current = controller;
    setFilterOptionsStatus(isInitialLoad ? 'initial-loading' : 'refresh-loading');
    setFilterOptionsError('');

    try {
      const result = await fetchValidationLabJson(
        '/validation-lab/filter-options',
        { index_code: requestUniverse },
        controller.signal
      );

      if (requestId !== filterOptionsRequestIdRef.current || controller.signal.aborted) {
        return;
      }
      if (!result || result.index_code !== requestUniverse) {
        throw new Error('Strategy Lab filter choices returned a different universe.');
      }

      const countries = normalizeAuthoritativeOptions(result.countries).map(
        (value) => canonicalizeCountry(value) || value
      );
      const sectors = normalizeAuthoritativeOptions(result.sectors);
      const industries = normalizeAuthoritativeOptions(result.industries);
      const records = Array.isArray(result.records) ? result.records : [];
      const nextHierarchy = createHierarchy(records);
      const provisional = provisionalSelectionsRef.current;
      setFilterOptions({
        indexCode: requestUniverse,
        universe_label: result.universe_label,
        ranking_source: result.ranking_source,
        ranking_universe_code: result.ranking_universe_code,
        benchmark_label: result.benchmark_label,
        total_unique_snapshots: result.total_unique_snapshots,
        earliest_snapshot_date: result.earliest_snapshot_date,
        latest_snapshot_date: result.latest_snapshot_date,
        countries,
        sectors,
        industries,
        records,
      });
      setSelectedFilters((currentFilters) => {
        if (currentFilters.indexCode !== requestUniverse) {
          return currentFilters;
        }

        const candidates = provisional?.indexCode === requestUniverse
          ? provisional
          : currentFilters;
        const next = nextHierarchy.clearDependentFilters({
          universe: requestUniverse,
          country: candidates.country,
          sector: candidates.sector,
          industry: candidates.industry,
        });
        return {
          indexCode: requestUniverse,
          country: countries.includes(next.country || '') ? (next.country || '') : '',
          sector: sectors.includes(next.sector || '') ? (next.sector || '') : '',
          industry: industries.includes(next.industry || '') ? (next.industry || '') : '',
        };
      });
      if (provisionalSelectionsRef.current?.indexCode === requestUniverse) {
        provisionalSelectionsRef.current = null;
      }
      setFilterOptionsStatus('ready');
    } catch (error) {
      if (controller.signal.aborted || requestId !== filterOptionsRequestIdRef.current) {
        return;
      }

      setFilterOptions(EMPTY_FILTER_OPTIONS);
      setSelectedFilters((currentFilters) => (
        currentFilters.indexCode === requestUniverse
          ? { indexCode: requestUniverse, country: '', sector: '', industry: '' }
          : currentFilters
      ));
      if (provisionalSelectionsRef.current?.indexCode === requestUniverse) {
        provisionalSelectionsRef.current = null;
      }
      setFilterOptionsError(investorErrorMessage(error));
      setFilterOptionsStatus('unavailable');
    } finally {
      if (requestId === filterOptionsRequestIdRef.current) {
        filterOptionsAbortRef.current = null;
      }
    }
  };

  const resetComparisonState = () => {
    setComparisonData(null);
    setComparisonError('');
    setComparisonLoading(false);
  };

  const resetStrategyState = () => {
    setStrategyResultsByMode(null);
    setStrategyError('');
    setStrategyLoading(false);
    setActiveStrategyMode('balanced');
    setSelectedStrategyCohort(10);
  };

  const resetOptimalTopNState = () => {
    setOptimalTopNData(null);
    setOptimalTopNError('');
    setOptimalTopNLoading(false);
  };

  const resetSynthesisState = () => {
    setSynthesisError('');
    setSynthesisLoading(false);
  };

  const loadUserInfo = useCallback(async (signal) => {
    setLoadingUserInfo(true);
    setUserInfoError('');

    try {
      const response = await apiFetch(`${API_BASE_URL}/me`, {
        method: 'GET',
        signal,
      });

      if (!response.ok) {
        throw await validationLabRequestError(response);
      }

      const userData = await response.json();
      if (signal?.aborted) {
        return;
      }
      setUserInfo(userData);
    } catch (err) {
      if (signal?.aborted) {
        return;
      }
      setUserInfo(null);
      setUserInfoError(investorErrorMessage(err));
    } finally {
      if (!signal?.aborted) {
        setLoadingUserInfo(false);
      }
    }
  }, []);

  useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
    comparisonDataRef.current = comparisonData;
    strategyResultsByModeRef.current = strategyResultsByMode;
    activeStrategyModeRef.current = activeStrategyMode;
    selectedStrategyCohortRef.current = selectedStrategyCohort;
    optimalTopNDataRef.current = optimalTopNData;
    selectedPortfolioObjectiveRef.current = selectedPortfolioObjective;
    synthesisDataRef.current = synthesisData;
  }, [
    selectedFilters,
    comparisonData,
    strategyResultsByMode,
    activeStrategyMode,
    selectedStrategyCohort,
    optimalTopNData,
    selectedPortfolioObjective,
    synthesisData,
  ]);

  useEffect(() => {
    if (loadingUserInfo) {
      return;
    }

    const userId = getValidationLabUserId(userInfo);
    const previousUserId = persistedUserIdRef.current;
    if (previousUserId && previousUserId !== userId) {
      clearValidationLabResults(previousUserId);
      persistedSnapshotRef.current = null;
      setSelectedFilters({ indexCode: 'SP500', country: '', sector: '', industry: '' });
      setComparisonData(null);
      setStrategyResultsByMode(null);
      setOptimalTopNData(null);
      setSynthesisData(null);
      setActiveStrategyMode('balanced');
      setSelectedStrategyCohort(10);
      setSelectedPortfolioObjective('balanced');
    }

    if (!userId) {
      persistedUserIdRef.current = null;
      persistedSnapshotRef.current = null;
      return;
    }

    if (previousUserId === userId) {
      return;
    }

    persistedUserIdRef.current = userId;
    const snapshot = loadValidationLabResults(userId);
    persistedSnapshotRef.current = snapshot;
    if (!snapshot) {
      return;
    }

    if (snapshot.selectedFilters?.indexCode) {
      setSelectedFilters({
        indexCode: snapshot.selectedFilters.indexCode,
        country: snapshot.selectedFilters.country || '',
        sector: snapshot.selectedFilters.sector || '',
        industry: snapshot.selectedFilters.industry || '',
      });
    }
    if (hasOwn(snapshot, 'comparisonData')) {
      setComparisonData(snapshot.comparisonData || null);
    }
    if (hasOwn(snapshot, 'strategyResultsByMode')) {
      setStrategyResultsByMode(snapshot.strategyResultsByMode || null);
    }
    if (snapshot.activeStrategyMode) {
      setActiveStrategyMode(snapshot.activeStrategyMode);
    }
    if (Number.isInteger(snapshot.selectedStrategyCohort)) {
      setSelectedStrategyCohort(snapshot.selectedStrategyCohort);
    }
    if (hasOwn(snapshot, 'optimalTopNData')) {
      setOptimalTopNData(snapshot.optimalTopNData || null);
    }
    if (snapshot.selectedPortfolioObjective) {
      setSelectedPortfolioObjective(snapshot.selectedPortfolioObjective);
    }
    if (hasOwn(snapshot, 'synthesisData')) {
      setSynthesisData(snapshot.synthesisData || null);
    }
  }, [loadingUserInfo, userInfo]);

  const handleClearComparison = () => {
    cancelComparisonRequest();
    setComparisonData(null);
    setComparisonError('');
    setComparisonLoading(false);
    persistResultsSnapshot({ comparisonData: null });
  };

  const handleClearSimulation = () => {
    cancelStrategyRequests();
    setStrategyResultsByMode(null);
    setStrategyError('');
    setStrategyLoading(false);
    setActiveStrategyMode('balanced');
    setSelectedStrategyCohort(10);
    persistResultsSnapshot({
      strategyResultsByMode: null,
      activeStrategyMode: 'balanced',
      selectedStrategyCohort: 10,
    });
  };

  const handleClearOptimalTopN = () => {
    cancelOptimalTopNRequest();
    setOptimalTopNData(null);
    setOptimalTopNError('');
    setOptimalTopNLoading(false);
    persistResultsSnapshot({ optimalTopNData: null });
  };

  useEffect(() => {
    if (synthesisData && !isSupportedStrategyPlanUniverse(synthesisData.index_code)) {
      setSynthesisData(null);
      setSynthesisExportError('');
    }
  }, [synthesisData]);

  useEffect(() => {
    logFeatureView('validation_lab', 'validation_lab');
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadUserInfo(controller.signal);
    return () => controller.abort();
  }, [loadUserInfo]);

  useEffect(() => {
    if (!hierarchyReady) {
      return;
    }
    const next = hierarchy.clearDependentFilters({
      universe: selectedUniverse,
      country: selectedCountry,
      sector: selectedSector,
      industry: selectedIndustry,
    });

    if (
      (next.country || '') !== selectedCountry ||
      (next.sector || '') !== selectedSector ||
      (next.industry || '') !== selectedIndustry
    ) {
      setSelectedFilters((currentFilters) => ({
        ...currentFilters,
        country: next.country || '',
        sector: next.sector || '',
        industry: next.industry || '',
      }));
    }
  }, [
    hierarchy,
    hierarchyReady,
    selectedUniverse,
    selectedCountry,
    selectedSector,
    selectedIndustry,
  ]);

  useEffect(() => {
    if (loadingUserInfo) {
      setFilterOptionsStatus('initial-loading');
      setFilterOptionsError('');
      return () => cancelFilterOptionsRequest();
    }

    if (!userInfo) {
      cancelFilterOptionsRequest();
      setFilterOptions(EMPTY_FILTER_OPTIONS);
      setSelectedFilters((currentFilters) => ({
        ...currentFilters,
        country: '',
        sector: '',
        industry: '',
      }));
      setFilterOptionsStatus('unavailable');
      setFilterOptionsError(
        userInfoError || 'Your session has expired. Sign in again to use Strategy Lab.'
      );
      return () => cancelFilterOptionsRequest();
    }

    if (userInfo.plan_type !== 'unlimited') {
      cancelFilterOptionsRequest();
      setFilterOptions(EMPTY_FILTER_OPTIONS);
      setSelectedFilters((currentFilters) => ({
        ...currentFilters,
        country: '',
        sector: '',
        industry: '',
      }));
      setFilterOptionsStatus('unavailable');
      setFilterOptionsError('Strategy Lab requires an active Premium subscription.');
      return () => cancelFilterOptionsRequest();
    }

    loadFilterOptions(selectedUniverse);
    return () => cancelFilterOptionsRequest();
  }, [selectedUniverse, loadingUserInfo, userInfo, userInfoError]);

  useEffect(() => {
    return () => {
      cancelFilterOptionsRequest();
      cancelComparisonRequest();
      cancelStrategyRequests();
      cancelOptimalTopNRequest();
      cancelSynthesisRequest(false);
      cancelSynthesisExportRequest(false);
      cancelExportRequest();
    };
  }, []);

  const handleUniverseChange = (event) => {
    const nextUniverse = event.target.value;

    if (nextUniverse === selectedUniverse) {
      return;
    }

    const priorSelections = provisionalSelectionsRef.current || selectedFilters;
    provisionalSelectionsRef.current = {
      indexCode: nextUniverse,
      country: priorSelections.country,
      sector: priorSelections.sector,
      industry: priorSelections.industry,
    };
    cancelActiveRequestsForFilterChange();

    resetComparisonState();
    resetStrategyState();
    resetOptimalTopNState();
    resetSynthesisState();
    setSynthesisExportError('');
    setExportError('');
    setExportingPDF(false);
    setFilterOptions(EMPTY_FILTER_OPTIONS);
    setFilterOptionsStatus('refresh-loading');
    setFilterOptionsError('');
    setSelectedFilters({ indexCode: nextUniverse, country: '', sector: '', industry: '' });
  };

  const handleCountryChange = (event) => {
    if (filterOptionsStatus !== 'ready' || !hierarchyReady) {
      return;
    }
    cancelActiveRequestsForFilterChange();
    const next = hierarchy.clearDependentFilters({
      universe: selectedUniverse,
      country: event.target.value,
      sector: selectedSector,
      industry: selectedIndustry,
    });
    setSelectedFilters((currentFilters) => ({
      ...currentFilters,
      country: next.country || '',
      sector: next.sector || '',
      industry: next.industry || '',
    }));
  };

  const handleSectorChange = (event) => {
    if (filterOptionsStatus !== 'ready' || !hierarchyReady) {
      return;
    }
    cancelActiveRequestsForFilterChange();
    const next = hierarchy.clearDependentFilters({
      universe: selectedUniverse,
      country: selectedCountry,
      sector: event.target.value,
      industry: selectedIndustry,
    });
    setSelectedFilters((currentFilters) => ({
      ...currentFilters,
      sector: next.sector || '',
      industry: next.industry || '',
    }));
  };

  const handleIndustryChange = (event) => {
    if (filterOptionsStatus !== 'ready') {
      return;
    }
    cancelActiveRequestsForFilterChange();
    setSelectedFilters((currentFilters) => ({
      ...currentFilters,
      industry: event.target.value,
    }));
  };

  const handleRetryFilterOptions = () => {
    cancelActiveRequestsForFilterChange();
    if (!userInfo || userInfo.plan_type !== 'unlimited') {
      const controller = new AbortController();
      loadUserInfo(controller.signal);
      return;
    }
    loadFilterOptions(selectedUniverse, { retry: true });
  };

  const handleRunComparison = async () => {
    if (filterSelectionPending) {
      return;
    }
    const requestFilters = { ...selectedFilters };
    const requestUniverse = requestFilters.indexCode;
    const { requestId, controller } = beginComparisonRequest();

    setComparisonLoading(true);
    setComparisonError('');

    try {
      const result = await fetchValidationLabJson(
        '/validation-lab/cohort-comparison',
        buildValidationLabRequestParams(requestFilters),
        controller.signal
      );

      if (requestId !== comparisonRequestIdRef.current) {
        return;
      }

      requireUniverseResponse(result, requestUniverse, 'cohorts', 'Run Comparison');
      requireFilterResponse(result, requestFilters, 'Run Comparison');
      setComparisonData(result);
      persistResultsSnapshot({ selectedFilters: requestFilters, comparisonData: result });
    } catch (err) {
      if (controller.signal.aborted || requestId !== comparisonRequestIdRef.current) {
        return;
      }

      setComparisonError(investorErrorMessage(err));
    } finally {
      if (requestId === comparisonRequestIdRef.current) {
        setComparisonLoading(false);
        comparisonAbortRef.current = null;
      }
    }
  };

  const handleRunSimulation = async () => {
    if (filterSelectionPending) {
      return;
    }
    const requestFilters = { ...selectedFilters };
    const requestUniverse = requestFilters.indexCode;
    const { requestId, controllers } = beginStrategyRequests();

    setStrategyLoading(true);
    setStrategyError('');

    try {
      const results = await Promise.allSettled(
        STRATEGY_MODES.map((mode, index) =>
          fetchValidationLabJson(
            '/validation-lab/rebalanced-strategy',
            buildValidationLabRequestParams(requestFilters, {
              weighting_mode: mode.value,
            }),
            controllers[index].signal
          ).then((result) => ({ mode: mode.value, result }))
        )
      );

      if (requestId !== strategyRequestIdRef.current) {
        return;
      }

      const nextResultsByMode = {};
      const failedModes = [];
      const failedReasons = [];

      results.forEach((item, index) => {
        const modeOption = STRATEGY_MODES[index];

        if (item.status !== 'fulfilled') {
          if (!controllers[index].signal.aborted) {
            failedModes.push(modeOption.label);
            failedReasons.push(investorErrorMessage(item.reason));
          }
          return;
        }

        try {
          requireUniverseResponse(
            item.value.result,
            requestUniverse,
            'cohorts',
            `${modeOption.label} simulation`
          );
          requireFilterResponse(
            item.value.result,
            requestFilters,
            `${modeOption.label} simulation`
          );
        } catch (err) {
          failedModes.push(modeOption.label);
          failedReasons.push(investorErrorMessage(err));
          return;
        }

        if (
          item.value.result.weighting_mode &&
          item.value.result.weighting_mode !== modeOption.value
        ) {
          failedModes.push(modeOption.label);
          return;
        }

        nextResultsByMode[modeOption.value] = item.value.result;
      });

      if (failedModes.length > 0 || STRATEGY_MODES.some((mode) => !nextResultsByMode[mode.value])) {
        setStrategyError(
          `Run Simulation failed for ${failedModes.join(', ') || 'one or more weighting modes'}${
            failedReasons[0] ? `: ${failedReasons[0]}` : '.'
          } The previous successful result is retained.`
        );
        return;
      }

      setStrategyResultsByMode(nextResultsByMode);
      setActiveStrategyMode('balanced');
      const nextSelectedCohort =
        getDisplayedStrategyCohorts(nextResultsByMode.balanced)[0]?.rank_threshold || 10;
      setSelectedStrategyCohort(nextSelectedCohort);
      persistResultsSnapshot({
        selectedFilters: requestFilters,
        strategyResultsByMode: nextResultsByMode,
        activeStrategyMode: 'balanced',
        selectedStrategyCohort: nextSelectedCohort,
      });
    } catch (err) {
      if (requestId !== strategyRequestIdRef.current) {
        return;
      }

      setStrategyError(investorErrorMessage(err));
    } finally {
      if (requestId === strategyRequestIdRef.current) {
        setStrategyLoading(false);
        strategyAbortRefs.current = [];
      }
    }
  };

  const handleStrategyModeSelect = (modeValue) => {
    const nextStrategyData = strategyResultsByMode?.[modeValue];
    const nextThresholds = getDisplayedStrategyCohorts(nextStrategyData).map(
      (cohort) => cohort.rank_threshold
    );

    setActiveStrategyMode(modeValue);

    if (nextThresholds.length > 0 && !nextThresholds.includes(selectedStrategyCohort)) {
      setSelectedStrategyCohort(nextThresholds[0]);
      persistResultsSnapshot({
        activeStrategyMode: modeValue,
        selectedStrategyCohort: nextThresholds[0],
      });
      return;
    }

    persistResultsSnapshot({ activeStrategyMode: modeValue });
  };

  const handleStrategyCohortSelect = (threshold) => {
    setSelectedStrategyCohort(threshold);
    persistResultsSnapshot({ selectedStrategyCohort: threshold });
  };

  const handleRunOptimalTopN = async () => {
    if (filterSelectionPending) {
      return;
    }
    const requestFilters = { ...selectedFilters };
    const requestUniverse = requestFilters.indexCode;
    const { requestId, controller } = beginOptimalTopNRequest();

    setOptimalTopNLoading(true);
    setOptimalTopNError('');

    try {
      const result = await fetchValidationLabJson(
        '/validation-lab/optimal-top-n',
        buildValidationLabRequestParams(requestFilters),
        controller.signal
      );

      if (requestId !== optimalTopNRequestIdRef.current) {
        return;
      }

      requireUniverseResponse(result, requestUniverse, 'results', 'Find Optimal Top N');
      requireFilterResponse(result, requestFilters, 'Find Optimal Top N');
      setOptimalTopNData(result);
      persistResultsSnapshot({ selectedFilters: requestFilters, optimalTopNData: result });
    } catch (err) {
      if (controller.signal.aborted || requestId !== optimalTopNRequestIdRef.current) {
        return;
      }

      setOptimalTopNError(investorErrorMessage(err));
    } finally {
      if (requestId === optimalTopNRequestIdRef.current) {
        setOptimalTopNLoading(false);
        optimalTopNAbortRef.current = null;
      }
    }
  };

  const handlePortfolioObjectiveChange = (objectiveValue) => {
    cancelSynthesisRequest();
    setSynthesisError('');
    setSynthesisExportError('');
    setSelectedPortfolioObjective(objectiveValue);
    persistResultsSnapshot({ selectedPortfolioObjective: objectiveValue });
  };

  const handleClearSynthesis = () => {
    cancelSynthesisRequest();
    cancelSynthesisExportRequest();
    setSynthesisData(null);
    setSynthesisError('');
    setSynthesisExportError('');
    persistResultsSnapshot({ synthesisData: null });
  };

  const handleRunStrategySynthesis = async () => {
    if (synthesisLoadingRef.current || filterSelectionPending) {
      return;
    }

    const requestFilters = { ...selectedFilters };
    const requestUniverse = requestFilters.indexCode;
    const { requestId, controller } = beginSynthesisRequest();

    setSynthesisLoading(true);
    setSynthesisError('');

    try {
      const result = await fetchValidationLabJson(
        '/validation-lab/strategy-synthesis',
        buildValidationLabRequestParams(requestFilters),
        controller.signal
      );

      if (requestId !== synthesisRequestIdRef.current) {
        return;
      }

      if (!result || result.index_code !== requestUniverse) {
        throw new Error('Strategy Synthesis returned results for a different universe.');
      }

      if (result.ranking_source !== SCORING_SOURCE_TECHNICAL_NAME) {
        throw new Error('Strategy Synthesis returned an unexpected ranking source.');
      }

      if (result.ranking_universe_code !== 'ALL') {
        throw new Error('Strategy Synthesis returned an unexpected ranking universe.');
      }

      if (result.selected_universe_code !== requestUniverse) {
        throw new Error('Strategy Synthesis returned results for a different selected universe.');
      }
      requireFilterResponse(result, requestFilters, 'Strategy Synthesis');

      if (!Array.isArray(result.mode_summaries) || result.mode_summaries.length !== STRATEGY_MODES.length) {
        throw new Error('Strategy Synthesis returned an incomplete mode summary.');
      }

      if (!result.cross_mode_comparison) {
        throw new Error('Strategy Synthesis returned an incomplete evidence payload.');
      }

      setSynthesisData(result);
      persistResultsSnapshot({ selectedFilters: requestFilters, synthesisData: result });
    } catch (err) {
      if (controller.signal.aborted || requestId !== synthesisRequestIdRef.current) {
        return;
      }

      setSynthesisError(investorErrorMessage(err));
    } finally {
      if (requestId === synthesisRequestIdRef.current) {
        setSynthesisLoading(false);
        synthesisLoadingRef.current = false;
        synthesisAbortRef.current = null;
      }
    }
  };

  const handleExportStrategySynthesisPDF = async () => {
    if (!displayedSynthesisData || synthesisExportingRef.current || filterSelectionPending) {
      return;
    }

    const displayedResult = displayedSynthesisData;
    const requestFilters = { ...selectedFilters };

    if (!isSupportedStrategyPlanUniverse(displayedResult.index_code)) {
      setSynthesisExportError('This Strategy Plan result uses an unsupported universe and cannot be exported.');
      return;
    }
    if (!resultMatchesFilters(displayedResult, requestFilters)) {
      setSynthesisExportError(
        'Run the Strategy Plan with the current filters before exporting it.'
      );
      return;
    }

    const { requestId, controller } = beginSynthesisExportRequest();

    setSynthesisExportingPDF(true);
    setSynthesisExportError('');

    try {
      const params = new URLSearchParams(
        buildValidationLabRequestParams(requestFilters)
      );
      params.set('weighting_mode', selectedPortfolioObjectiveOption.mode);
      const response = await apiFetch(`${API_BASE_URL}/validation-lab/strategy-synthesis/export-pdf?${params.toString()}`, {
        method: 'GET',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw await validationLabRequestError(response);
      }

      if (requestId !== synthesisExportRequestIdRef.current) {
        return;
      }

      const blob = await response.blob();
      const contentDisposition = response.headers?.get?.('Content-Disposition');
      const filename = getFilenameFromContentDisposition(
        contentDisposition,
        buildStrategySynthesisPdfFilename(displayedResult)
      );
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = filename;

      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(anchor);
    } catch (err) {
      if (controller.signal.aborted || requestId !== synthesisExportRequestIdRef.current) {
        return;
      }

      setSynthesisExportError(
        `Failed to export Full Statistical Report PDF: ${investorErrorMessage(err)}`
      );
    } finally {
      if (requestId === synthesisExportRequestIdRef.current) {
        setSynthesisExportingPDF(false);
        synthesisExportingRef.current = false;
        synthesisExportAbortRef.current = null;
      }
    }
  };

  const handleExportValidationLabPDF = async () => {
    if (filterSelectionPending) {
      return;
    }
    const requestFilters = { ...selectedFilters };
    const requestUniverse = requestFilters.indexCode;
    const { requestId, controller } = beginExportRequest();

    setExportingPDF(true);
    setExportError('');

    try {
      const params = new URLSearchParams(
        buildValidationLabRequestParams(requestFilters)
      );
      const response = await apiFetch(`${API_BASE_URL}/validation-lab/export-pdf?${params.toString()}`, {
        method: 'GET',
        signal: controller.signal,
      });

      if (!response.ok) {
        throw await validationLabRequestError(response);
      }

      if (requestId !== exportRequestIdRef.current) {
        return;
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = downloadUrl;
      anchor.download = buildValidationLabPdfFilename(requestUniverse);

      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(anchor);
    } catch (err) {
      if (controller.signal.aborted || requestId !== exportRequestIdRef.current) {
        return;
      }

      setExportError(`Failed to export PDF: ${investorErrorMessage(err)}`);
    } finally {
      if (requestId === exportRequestIdRef.current) {
        setExportingPDF(false);
        exportAbortRef.current = null;
      }
    }
  };

  return (
    <div className="validation-lab-page authenticated-page">
      <div className="validation-lab-container authenticated-page-content">
        <div className="validation-lab-header">
          <div className="validation-lab-header-content">
            <h1 className="validation-lab-title">Strategy Lab</h1>
            <p className="validation-lab-subtitle">Premium model-level strategy evidence and performance analysis</p>
          </div>
        </div>

        <div className="validation-lab-scope-card">
          <div className="validation-lab-scope-row">
            <div className="validation-lab-scope-copy">
              <h3 className="validation-lab-scope-title">Validation Universe</h3>
              <p className="validation-lab-scope-text">
                Strategy Lab uses the same indexed universe choices as Run Scan, including U.S., Canadian, and combined indexed tickers.
              </p>
            </div>
            <div
              className={`validation-lab-filter-controls${filterOptionsLoading ? ' validation-lab-filter-controls--loading' : ''}`}
              aria-busy={filterOptionsLoading}
            >
              <label className="validation-lab-select-group" htmlFor="validation-lab-country">
                <span className="validation-lab-select-label">Country</span>
                <select
                  id="validation-lab-country"
                  className={`validation-lab-select${filterOptionsLoading ? ' validation-lab-select--loading' : ''}`}
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  disabled={filterOptionsStatus !== 'ready' || countryOptions.length === 0}
                >
                  <option value="">All Countries</option>
                  {countryOptions.map((country) => (
                    <option key={country} value={country}>{country}</option>
                  ))}
                </select>
              </label>
              <label className="validation-lab-select-group" htmlFor="validation-lab-universe">
                <span className="validation-lab-select-label">Universe</span>
                <select
                  id="validation-lab-universe"
                  className={`validation-lab-select${filterOptionsLoading ? ' validation-lab-select--loading' : ''}`}
                  value={selectedUniverse}
                  onChange={handleUniverseChange}
                >
                  {VALIDATION_UNIVERSE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="validation-lab-select-group" htmlFor="validation-lab-sector">
                <span className="validation-lab-select-label">Sector</span>
                <select
                  id="validation-lab-sector"
                  className={`validation-lab-select${filterOptionsLoading ? ' validation-lab-select--loading' : ''}`}
                  value={selectedSector}
                  onChange={handleSectorChange}
                  disabled={filterOptionsStatus !== 'ready' || sectorOptions.length === 0}
                >
                  <option value="">All Sectors</option>
                  {sectorOptions.map((sector) => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
              </label>
              <label className="validation-lab-select-group" htmlFor="validation-lab-industry">
                <span className="validation-lab-select-label">Industry</span>
                <select
                  id="validation-lab-industry"
                  className={`validation-lab-select${filterOptionsLoading ? ' validation-lab-select--loading' : ''}`}
                  value={selectedIndustry}
                  onChange={handleIndustryChange}
                  disabled={filterOptionsStatus !== 'ready' || industryOptions.length === 0}
                >
                  <option value="">All Industries</option>
                  {industryOptions.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
          {validationUniverseMetadata && (
            <p className="validation-lab-metadata">
              <strong>Universe:</strong> {validationUniverseMetadata.universe_label}
              {' '}· <strong>Scoring Source:</strong> {getScoringSourceDisplay(validationUniverseMetadata)}
              {' '}· <strong>Selected Filter:</strong> {getSelectedFilterDisplay(validationUniverseMetadata)}
              {' '}· <strong>Benchmark:</strong> {BENCHMARK_DISPLAY_DESCRIPTION}
              {' '}· <strong>Validation Window:</strong> {validationUniverseMetadata.earliest_snapshot_date} to {validationUniverseMetadata.latest_snapshot_date}
              {' '}({validationUniverseMetadata.total_unique_snapshots} rolling annual snapshots)
            </p>
          )}
          {filterOptionsStatus === 'initial-loading' && (
            <div className="validation-lab-filter-status" role="status">
              {loadingUserInfo
                ? 'Checking Strategy Lab access and loading Country, Sector, and Industry choices.'
                : 'Loading Country, Sector, and Industry choices.'}
            </div>
          )}
          {filterOptionsStatus === 'refresh-loading' && (
            <div className="validation-lab-filter-status" role="status">
              Refreshing Country, Sector, and Industry choices for {selectedUniverseOption.label}.
            </div>
          )}
          {filterOptionsStatus === 'ready' && countryOptions.length === 0 && (
            <div className="validation-lab-filter-status" role="status">
              No Country choices are available for the active publication. Reports remain unfiltered by Country.
            </div>
          )}
          {filterOptionsStatus === 'ready' && sectorOptions.length === 0 && (
            <div className="validation-lab-filter-status" role="status">
              No Sector choices are available for the current Country and Universe selection. Reports remain unfiltered by Sector.
            </div>
          )}
          {filterOptionsStatus === 'ready' && industryOptions.length === 0 && (
            <div className="validation-lab-filter-status" role="status">
              No Industry choices are available for the current Country, Universe, and Sector selection. Reports remain unfiltered by Industry.
            </div>
          )}
          {filterOptionsStatus === 'unavailable' && (
            <div className="validation-lab-filter-error" role="alert">
              <span>{filterOptionsError}</span>
              <button
                type="button"
                className="validation-lab-btn-secondary validation-lab-filter-retry"
                onClick={handleRetryFilterOptions}
              >
                Retry filter choices
              </button>
            </div>
          )}

        </div>

        <div className="validation-lab-flow-guide" aria-label="Strategy Lab workflow">
          <ol className="validation-lab-flow-steps">
            <li>
              <span>1.</span>
              {' '}
              Compare Cohorts
            </li>
            <li>
              <span>2.</span>
              {' '}
              Test Annual Portfolios
            </li>
            <li>
              <span>3.</span>
              {' '}
              Understand Top N
            </li>
            <li>
              <span>4.</span>
              {' '}
              Compare Strategies and Build a Plan
            </li>
          </ol>
          <p>
            Steps 1–3 explain historical evidence and Step 4 compares Fixed Top N with Walk-Forward over the same years to build the plan.
          </p>
        </div>

        <div className="validation-lab-phase-intro validation-lab-phase-intro-historical">
          <span className="validation-lab-phase-badge validation-lab-phase-badge-historical">PHASE 1</span>
          <h2>Understand the Historical Evidence</h2>
          <p>
            Each yearly snapshot checks which stocks were inside the selected Top N. If the same stock qualifies again in another year, that counts as a new Top N result.
          </p>
          <p>
            <strong>Example — 3 stocks qualifying over 3 yearly snapshots, viewed in 2030:</strong>
          </p>
          <div className="validation-lab-table-wrapper">
            <table className="validation-lab-table">
              <thead className="validation-lab-table-header">
                <tr>
                  <th className="validation-lab-th">Snapshot Year</th>
                  <th className="validation-lab-th">Stocks in Top N</th>
                  <th className="validation-lab-th">1Y Result by 2030</th>
                  <th className="validation-lab-th">10Y Result by 2030</th>
                </tr>
              </thead>
              <tbody className="validation-lab-table-body">
                <tr className="validation-lab-tr">
                  <td className="validation-lab-td">2020</td>
                  <td className="validation-lab-td">AAPL, MSFT, GOOGL</td>
                  <td className="validation-lab-td">3 completed</td>
                  <td className="validation-lab-td">3 completed</td>
                </tr>
                <tr className="validation-lab-tr">
                  <td className="validation-lab-td">2021</td>
                  <td className="validation-lab-td">AAPL, MSFT, GOOGL</td>
                  <td className="validation-lab-td">3 completed</td>
                  <td className="validation-lab-td">Not yet</td>
                </tr>
                <tr className="validation-lab-tr">
                  <td className="validation-lab-td">2022</td>
                  <td className="validation-lab-td">AAPL, MSFT, GOOGL</td>
                  <td className="validation-lab-td">3 completed</td>
                  <td className="validation-lab-td">Not yet</td>
                </tr>
                <tr className="validation-lab-tr">
                  <td className="validation-lab-td">Total</td>
                  <td className="validation-lab-td">9 Top N qualifications</td>
                  <td className="validation-lab-td">9 completed 1Y returns</td>
                  <td className="validation-lab-td">3 completed 10Y returns</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            A 1-year return needs only one year after qualification, so all 9 Top N qualifications above can be measured by 2030.
          </p>
          <p>
            A 10-year return needs ten full years after qualification. By 2030, only the three stocks that qualified in 2020 have completed that period.
          </p>
          <ul className="validation-lab-info-list">
            <li>
              <strong>Completed Returns</strong> = how many past Top N qualifications have finished the selected return period.
            </li>
            <li>
              <strong>Win Rate</strong> = what percentage of those completed Top N returns were positive.
            </li>
            <li>
              <strong>Why it matters:</strong> More completed Top N returns means more historical evidence behind the result, which helps judge how much confidence to place in it when building the final Strategy Plan.
            </li>
          </ul>
        </div>

        <div className="validation-lab-section validation-lab-strategy-section">
          <div className="validation-lab-section-header">
            <div className="validation-lab-report-heading">
              <span className="validation-lab-analysis-badge validation-lab-analysis-badge-historical">HISTORICAL ANALYSIS</span>
              <h3 className="validation-lab-section-title">1. Cohort Performance Comparison</h3>
              <p className="validation-lab-section-description">
                Did higher-ranked groups perform better?
              </p>
              <p className="validation-lab-report-definition">
                <strong>Tested Top N</strong> is the cutoff being tested, such as Top 10, Top 25, Top 50, or Top 100. <strong>Actual Stocks Used</strong> is how many stocks were actually available after the selected filters were applied.
              </p>
              <p className="validation-lab-report-definition">
                Example: if you test <strong>Top 50</strong> but only <strong>43 stocks</strong> qualify, the model uses those 43.
              </p>
              <p className="validation-lab-report-definition">
                If a larger Tested Top N ends up with the same effective cohort metadata and the same historical results as a smaller Tested Top N, the larger row adds no new information.
              </p>
              
            </div>
            <button
              onClick={handleRunComparison}
              disabled={comparisonLoading || filterSelectionPending}
              className="validation-lab-btn-primary"
            >
              {comparisonLoading ? `Running ${selectedUniverseOption.label} comparison...` : 'Run Comparison'}
            </button>
          </div>

          {(comparisonData || comparisonError) && (
            <div className="validation-lab-synthesis-actions">
              <button
                type="button"
                onClick={handleClearComparison}
                disabled={comparisonLoading}
                className="validation-lab-btn-secondary"
              >
                Clear Comparison
              </button>
            </div>
          )}

          {comparisonLoading && (
            <div className="validation-lab-status-message validation-lab-status-warning">
              <p>Building historical cohort comparisons for {selectedUniverseOption.label}.</p>
            </div>
          )}

          {comparisonError && (
            <div className="validation-lab-error">
              <svg className="validation-lab-error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{comparisonError} The last successful comparison result is retained until you rerun or clear it.</span>
            </div>
          )}

          {!comparisonLoading && !comparisonError && !comparisonData && (
            <div className="validation-lab-empty-card">
              <p className="validation-lab-empty-message">
                Run this analysis for {selectedUniverseOption.label}.
              </p>
            </div>
          )}

          {comparisonData && (
            (() => {
              const comparisonHorizons = getCohortForwardHorizons(comparisonData);
              const displayedComparisonCohorts = getDisplayedComparisonCohorts(
                comparisonData,
                comparisonHorizons
              );
              return (
            <>
             

              <div className="validation-lab-table-wrapper validation-lab-cohort-comparison-scroll">
                <table className="validation-lab-table validation-lab-cohort-comparison-table">
                  <thead className="validation-lab-table-header">
                    <tr>
                      <th className="validation-lab-th validation-lab-th-cohort">Tested Top N</th>
                      {comparisonHorizons.map((horizonYears) => (
                        <th key={horizonYears} className="validation-lab-th validation-lab-th-horizon">
                          {horizonYears}Y
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="validation-lab-table-body">
                    {displayedComparisonCohorts.map((cohort) => (
                      <tr key={cohort.rank_threshold} className="validation-lab-tr">
                        <td className="validation-lab-td validation-lab-td-cohort">
                          <div>Top {cohort.rank_threshold}</div>
                          {getComparisonActualStocksUsed(cohort) && (
                            <div className="validation-lab-metric-count">
                              Available: {getComparisonActualStocksUsed(cohort)}
                            </div>
                          )}
                        </td>
                        {comparisonHorizons.map((horizonYears) => {
                          const horizon = getHorizonKey(horizonYears);
                          const metrics = cohort.horizons?.[horizon] || {};
                          const availableReturns = Number(metrics.available_returns || 0);
                          return (
                            <td key={horizon} className="validation-lab-td validation-lab-td-metrics">
                              {availableReturns > 0 ? (
                                <div className="validation-lab-metrics-group">
                                  <div className={`validation-lab-metric-value ${getReturnClass(metrics.average_total_return_percent)}`}>
                                    Avg: {formatPercent(metrics.average_total_return_percent, { showSign: true })}
                                  </div>
                                  <div className="validation-lab-metric-secondary">
                                    Win Rate: {formatPercent(metrics.win_rate_percent)}
                                  </div>
                                  <div className="validation-lab-metric-count">
                                    ({availableReturns} completed)
                                  </div>
                                </div>
                              ) : (
                                <div className="validation-lab-metrics-group">
                                  <div className="validation-lab-metric-empty">—</div>
                                  <div className="validation-lab-metric-count">(0 completed)</div>
                                </div>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {comparisonData.validation_lab_interpretation && (
                <div className="validation-lab-interpretation-card">
                  <h3 className="validation-lab-interpretation-title">What the Results Mean?</h3>
                  <p className="validation-lab-interpretation-text">
                    {comparisonData.validation_lab_interpretation}
                  </p>
                </div>
              )}
            </>
              );
            })()
          )}
        </div>

        <div className="validation-lab-section validation-lab-strategy-section">
          <div className="validation-lab-section-header">
            <div className="validation-lab-report-heading">
              <span className="validation-lab-analysis-badge validation-lab-analysis-badge-historical">HISTORICAL ANALYSIS</span>
              <h3 className="validation-lab-section-title">2. Year-by-Year Portfolio Test</h3>
              <p className="validation-lab-report-definition">
                <strong>What is being tested?</strong>
              </p>
              <p className="validation-lab-section-description">
                Each year, the model builds a portfolio from the selected Top N stocks, holds it for one year, then uses the next yearly ranking to build the portfolio again.
              </p>
              <p className="validation-lab-report-definition">
                <strong>Example:</strong> If Top 10 is selected, the model uses the qualifying stocks available within that Top 10, holds them for one year, then checks the next year's ranking and builds the portfolio again.
              </p>
            </div>
            <button
              onClick={handleRunSimulation}
              disabled={strategyLoading || filterSelectionPending}
              className="validation-lab-btn-primary"
            >
              {strategyLoading ? `Running ${selectedUniverseOption.label} simulation...` : 'Run Simulation'}
            </button>
          </div>

          {(activeStrategyData || strategyError) && (
            <div className="validation-lab-synthesis-actions">
              <button
                type="button"
                onClick={handleClearSimulation}
                disabled={strategyLoading}
                className="validation-lab-btn-secondary"
              >
                Clear Simulation
              </button>
            </div>
          )}

          <div className="validation-lab-weighting-selector">
            <h4 className="validation-lab-subsection-title">How the Money Is Divided</h4>
            <div className="validation-lab-button-group">
              {STRATEGY_MODES.map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  disabled={!strategyResultsByMode || strategyLoading}
                  onClick={() => handleStrategyModeSelect(mode.value)}
                  className={`validation-lab-btn-mode ${activeStrategyMode === mode.value ? 'active' : ''}`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            <p className="validation-lab-mode-description">{activeStrategyOption.description}</p>
          </div>

          {strategyLoading && (
            <div className="validation-lab-status-message validation-lab-status-warning">
              <p>Running Balanced, Growth, and Conservative simulations together for {selectedUniverseOption.label}.</p>
            </div>
          )}

          {strategyError && (
            <div className="validation-lab-error">
              <svg className="validation-lab-error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{strategyError} The last successful simulation result is retained until you rerun or clear it.</span>
            </div>
          )}

          {!strategyLoading && !strategyError && !activeStrategyData && (
            <div className="validation-lab-empty-card">
              <p className="validation-lab-empty-message">
                Run this analysis for {selectedUniverseOption.label}.
              </p>
            </div>
          )}

          {activeStrategyData && (
            <>
              

              

              <div className="validation-lab-subsection">
                <h4 className="validation-lab-subsection-title">Overall Results</h4>
                <div className="validation-lab-table-wrapper">
                  <table className="validation-lab-table validation-lab-table-strategy validation-lab-table-wide-report">
                    <thead className="validation-lab-table-header">
                      <tr>
                        <th className="validation-lab-th validation-lab-th-cohort">Tested Top N</th>
                        <th className="validation-lab-th">Actual Stocks <br />Used</th>
                        <th className="validation-lab-th">Positive <br />Years</th>
                        <th className="validation-lab-th">Average 1Y <br />Return</th>
                        <th className="validation-lab-th">Total Compounded <br />Return</th>
                        <th className="validation-lab-th">S&amp;P 500 Compounded <br />Return</th>
                        <th className="validation-lab-th">Outperformance vs <br />S&amp;P 500</th>
                        <th className="validation-lab-th">Max Drawdown</th>
                      </tr>
                    </thead>
                    <tbody className="validation-lab-table-body">
                      {displayedActiveStrategyCohorts.map((cohort) => (
                        <tr key={cohort.rank_threshold} className="validation-lab-tr">
                          <td className="validation-lab-td validation-lab-td-cohort">Top {cohort.rank_threshold}</td>
                          <td className="validation-lab-td">
                            {getAnnualCohortHoldingsSummary(cohort).holdingsLabel || '—'}
                            {getAnnualCohortHoldingsSummary(cohort).eligibleLabel && (
                              <div className="validation-lab-metric-count">
                                Available {getAnnualCohortHoldingsSummary(cohort).eligibleCountLabel}
                              </div>
                            )}
                          </td>
                          <td className="validation-lab-td">{cohort.positive_period_count}</td>
                          <td className={`validation-lab-td validation-lab-td-return ${getReturnClass(cohort.average_annual_portfolio_return_percent)}`}>
                            {formatPercent(cohort.average_annual_portfolio_return_percent, { showSign: true })}
                          </td>
                          <td className={`validation-lab-td validation-lab-td-return ${getReturnClass(cohort.compounded_total_return_percent)}`}>
                            {formatPercent(cohort.compounded_total_return_percent, { showSign: true })}
                          </td>
                          <td className={`validation-lab-td validation-lab-td-return ${getReturnClass(cohort.benchmark_compounded_total_return_percent)}`}>
                            {formatPercent(cohort.benchmark_compounded_total_return_percent, { showSign: true })}
                          </td>
                          <td className={`validation-lab-td validation-lab-td-return ${getReturnClass(cohort.outperformance_vs_benchmark_percent)}`}>
                            {formatPercent(cohort.outperformance_vs_benchmark_percent, { showSign: true })}
                          </td>
                          <td className="validation-lab-td validation-lab-td-drawdown">
                            {formatPercent(cohort.max_drawdown_percent)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="validation-lab-interpretation-card">
                  <h3 className="validation-lab-interpretation-title">What the Results Mean?</h3>
                  <p className="validation-lab-interpretation-text">
                    Total Compounded Return shows the combined result of investing through the yearly periods one after another.
                  </p>
                  <p className="validation-lab-interpretation-text">
                    S&amp;P 500 Compounded Return shows the same compounded calculation for the S&amp;P 500 benchmark.
                  </p>
                  <p className="validation-lab-interpretation-text">
                    Outperformance vs S&amp;P 500 shows how much better or worse the portfolio performed than the S&amp;P 500 benchmark.
                  </p>
                  <p className="validation-lab-interpretation-text">
                    Max Drawdown shows the largest decline from a portfolio peak during the test.
                  </p>
                </div>
              </div>

              

              <div className="validation-lab-cohort-selector">
                <h4 className="validation-lab-subsection-title">See Each Year</h4>
                <div className="validation-lab-button-group">
                  {displayedActiveStrategyCohortThresholds.map((threshold) => (
                    <button
                      key={threshold}
                      onClick={() => handleStrategyCohortSelect(threshold)}
                      className={`validation-lab-btn-cohort ${selectedStrategyCohort === threshold ? 'active' : ''}`}
                    >
                      Top {threshold}
                    </button>
                  ))}
                </div>
              </div>

              {(() => {
                const selectedCohort = activeStrategyData.cohorts.find(
                  (cohort) => cohort.rank_threshold === selectedStrategyCohort
                );

                if (!selectedCohort) {
                  return null;
                }

                return (
                  <div className="validation-lab-subsection">
                    <h4 className="validation-lab-subsection-title">
                      {getStrategyModeOption(activeStrategyData.weighting_mode).label} Top {selectedStrategyCohort} — Year-by-Year Results
                    </h4>
                    {(getAnnualCohortHoldingsSummary(selectedCohort).holdingsLabel ||
                      getAnnualCohortHoldingsSummary(selectedCohort).eligibleLabel) && (
                      <p className="validation-lab-metadata">
                        {getAnnualCohortHoldingsSummary(selectedCohort).holdingsLabel && (
                          <>
                            <strong>Stocks Used:</strong>{' '}
                            {getAnnualCohortHoldingsSummary(selectedCohort).holdingsLabel}
                          </>
                        )}
                        {getAnnualCohortHoldingsSummary(selectedCohort).eligibleLabel && (
                          <>
                            {' '}· <strong>Available:</strong>{' '}
                            {getAnnualCohortHoldingsSummary(selectedCohort).eligibleCountLabel}
                          </>
                        )}
                      </p>
                    )}
                    {(selectedCohort.annual_periods || []).length === 0 ? (
                      <p className="validation-lab-empty-message">
                        No completed yearly results are available for this Top N.
                      </p>
                    ) : (
                      <div className="validation-lab-table-wrapper">
                        <table className="validation-lab-table validation-lab-table-wide-report">
                          <thead className="validation-lab-table-header">
                            <tr>
                              <th className="validation-lab-th validation-lab-th-date">Starting Date</th>
                              <th className="validation-lab-th">Stocks <br />Available</th>
                              <th className="validation-lab-th">Stocks <br />Used</th>
                              <th className="validation-lab-th">Portfolio 1Y <br />Return</th>
                              <th className="validation-lab-th">S&amp;P 500 1Y <br />Return</th>
                              <th className="validation-lab-th">Outperformance vs <br />S&amp;P 500</th>
                              <th className="validation-lab-th">Max Drawdown</th>
                            </tr>
                          </thead>
                          <tbody className="validation-lab-table-body">
                            {selectedCohort.annual_periods.map((period, index) => (
                              <tr key={`${selectedCohort.rank_threshold}-${period.snapshot_date}-${index}`} className="validation-lab-tr">
                                <td className="validation-lab-td validation-lab-td-date">
                                  {new Date(period.snapshot_date).toLocaleDateString()}
                                </td>
                                <td className="validation-lab-td">{period.stocks_in_cohort}</td>
                                <td className="validation-lab-td">{period.stocks_with_valid_returns}</td>
                                <td className={`validation-lab-td validation-lab-td-return ${getReturnClass(period.portfolio_return_percent)}`}>
                                  {formatPercent(period.portfolio_return_percent, { showSign: true })}
                                </td>
                                <td className={`validation-lab-td validation-lab-td-return ${getReturnClass(period.benchmark_1y_return_percent)}`}>
                                  {formatPercent(period.benchmark_1y_return_percent, { showSign: true })}
                                </td>
                                <td className={`validation-lab-td validation-lab-td-return ${getReturnClass(period.outperformance_vs_benchmark_percent)}`}>
                                  {formatPercent(period.outperformance_vs_benchmark_percent, { showSign: true })}
                                </td>
                                <td className="validation-lab-td validation-lab-td-drawdown">
                                  {formatPercent(period.max_drawdown_percent)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>

        <div className="validation-lab-section validation-lab-optimal-section">
          <div className="validation-lab-section-header">
            <div className="validation-lab-report-heading">
              <span className="validation-lab-analysis-badge validation-lab-analysis-badge-historical">HISTORICAL ANALYSIS</span>
              <h3 className="validation-lab-section-title">3. Find the Best Historical Top N</h3>
              <p className="validation-lab-report-definition">
                <strong>What is being tested?</strong>
              </p>
              <p className="validation-lab-section-description">
                The model compares different Top N sizes to see which one worked best historically for each way of dividing the money.
              </p>
              <p className="validation-lab-report-definition">
                <strong>Example:</strong> If Top 10 performed better than Top 20 and Top 30 during the historical test, Top 10 may be shown as the Best Historical Top N for that method.
              </p>
              <p className="validation-lab-report-definition">
                Tested Top N is the ranking cutoff being tested. The actual number of stocks used can be smaller if fewer qualifying stocks are available.
              </p>
              <p className="validation-lab-report-definition">
                The test checks the available Top N range for the selected universe: {displayedOptimalTopNRange}.
              </p>
            </div>
            <button
              onClick={handleRunOptimalTopN}
              disabled={optimalTopNLoading || filterSelectionPending}
              className="validation-lab-btn-primary"
            >
              {optimalTopNLoading ? `Finding ${selectedUniverseOption.label} optimal Top N...` : 'Find Optimal Top N'}
            </button>
          </div>

          {(optimalTopNData || optimalTopNError) && (
            <div className="validation-lab-synthesis-actions">
              <button
                type="button"
                onClick={handleClearOptimalTopN}
                disabled={optimalTopNLoading}
                className="validation-lab-btn-secondary"
              >
                Clear Optimal Top N
              </button>
            </div>
          )}

          {optimalTopNLoading && (
            <div className="validation-lab-status-message validation-lab-status-warning">
              <p>Testing {selectedOptimalTopNRange} across all three weighting modes for {selectedUniverseOption.label}.</p>
            </div>
          )}

          {optimalTopNError && (
            <div className="validation-lab-error">
              <svg className="validation-lab-error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
              <span>{optimalTopNError} The last successful optimal Top N result is retained until you rerun or clear it.</span>
            </div>
          )}

          {!optimalTopNLoading && !optimalTopNError && !optimalTopNData && (
            <div className="validation-lab-empty-card">
              <p className="validation-lab-empty-message">
                Run this analysis for {selectedUniverseOption.label}.
              </p>
            </div>
          )}

          {optimalTopNData && optimalTopNData.results.length === 0 && (
            <div className="validation-lab-empty-card">
              <p className="validation-lab-empty-message">
                No Optimal Top N rows are available for {optimalTopNData.universe_label}.
              </p>
            </div>
          )}

          {optimalTopNData && optimalTopNData.results.length > 0 && (
            <>
              

              <div className="validation-lab-table-wrapper">
                <table className="validation-lab-table validation-lab-table-optimal validation-lab-table-wide-report">
                  <thead className="validation-lab-table-header">
                    <tr>
                      <th className="validation-lab-th validation-lab-th-mode">How the Money Is <br />Divided</th>
                      <th className="validation-lab-th">Best Historical <br />Top N</th>
                      <th className="validation-lab-th">Positive <br />Years</th>
                      <th className="validation-lab-th">Average 1Y <br />Return</th>
                      <th className="validation-lab-th">Total Compounded <br />Return</th>
                      <th className="validation-lab-th">S&amp;P 500 Compounded <br />Return</th>
                      <th className="validation-lab-th">Outperformance vs <br />S&amp;P 500</th>
                      <th className="validation-lab-th">Max Drawdown</th>
                      <th className="validation-lab-th">Average Drawdown</th>
                    </tr>
                  </thead>
                  <tbody className="validation-lab-table-body">
                    {optimalTopNData.results.map((result) => (
                      <tr key={result.mode} className="validation-lab-tr">
                        <td className="validation-lab-td validation-lab-td-mode">
                          {getStrategyModeOption(result.mode).label}
                        </td>
                        <td className="validation-lab-td validation-lab-td-best-n">Top {result.best_top_n}</td>
                        <td className="validation-lab-td">{result.positive_periods != null ? result.positive_periods : '—'}</td>
                        <td className={`validation-lab-td validation-lab-td-return ${getReturnClass(result.average_annual_portfolio_return_percent)}`}>
                          {formatPercent(result.average_annual_portfolio_return_percent, { showSign: true })}
                        </td>
                        <td className={`validation-lab-td validation-lab-td-return ${getReturnClass(result.compounded_total_return_percent)}`}>
                          {formatPercent(result.compounded_total_return_percent, { showSign: true })}
                        </td>
                        <td className={`validation-lab-td validation-lab-td-return ${getReturnClass(result.benchmark_compounded_total_return_percent)}`}>
                          {formatPercent(result.benchmark_compounded_total_return_percent, { showSign: true })}
                        </td>
                        <td className={`validation-lab-td validation-lab-td-return ${getReturnClass(result.outperformance_vs_benchmark_percent)}`}>
                          {formatPercent(result.outperformance_vs_benchmark_percent, { showSign: true })}
                        </td>
                        <td className="validation-lab-td validation-lab-td-drawdown">
                          {formatPercent(result.max_drawdown_percent)}
                        </td>
                        <td className={`validation-lab-td validation-lab-td-drawdown ${isFiniteNumber(result.average_drawdown_percent) && result.average_drawdown_percent > 20 ? 'severe' : ''}`}>
                          {formatPercent(result.average_drawdown_percent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {optimalTopNInterpretation && (
                <div className="validation-lab-interpretation-card">
                  <h3 className="validation-lab-interpretation-title">What the Results Mean?</h3>
                  <p className="validation-lab-interpretation-text">
                    {optimalTopNInterpretation}
                  </p>
                </div>
              )}

              
            </>
          )}
        </div>

        {!loadingUserInfo && userInfo && userInfo.plan_type === 'unlimited' && (
          <div className="validation-lab-export-card">
            <button
              onClick={handleExportValidationLabPDF}
              disabled={exportingPDF || filterSelectionPending}
              className="validation-lab-btn-export"
            >
              <svg className="validation-lab-btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              {exportingPDF ? `Exporting ${selectedUniverseOption.label} PDF...` : 'Export Strategy Lab PDF'}
            </button>
            <p className="validation-lab-export-note">
              This PDF includes Cohort Performance, Year-by-Year Portfolio Test, and Best Historical Top N results for the selected filters.
            </p>
            {exportError && <p className="validation-lab-export-error">{exportError}</p>}
          </div>
        )}

        <div className="validation-lab-evidence-bridge">
          <h3>From Historical Evidence to a Practical Plan</h3>
          <p>
            The reports above show how rankings, portfolio size, how the money is divided, and yearly portfolio updates performed in the past. Step 4 compares the Best Historical Top N with Walk-Forward over the same years to build a practical plan.
          </p>
        </div>

        <StrategySynthesisSection
          selectedUniverseOption={selectedUniverseOption}
          portfolioObjectives={PORTFOLIO_OBJECTIVES}
          selectedPortfolioObjective={selectedPortfolioObjective}
          selectedPortfolioObjectiveOption={selectedPortfolioObjectiveOption}
          result={displayedSynthesisData}
          loading={synthesisLoading}
          error={synthesisError}
          exporting={synthesisExportingPDF}
          exportError={synthesisExportError}
          requestBlocked={filterSelectionPending}
          exportBlocked={
            filterSelectionPending ||
            !resultMatchesFilters(displayedSynthesisData, selectedFilters)
          }
          onRun={handleRunStrategySynthesis}
          onExport={handleExportStrategySynthesisPDF}
          onPortfolioObjectiveChange={handlePortfolioObjectiveChange}
          onClear={handleClearSynthesis}
        />
      </div>
    </div>
  );
}

export default ValidationLab;
